import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Paper,
  Typography,
} from "@material-ui/core";
import { Wrapper } from "../../components";
import Loading from "../../components/Loading/Loading";
import { ReviewTable } from "../../components/Reviews/ReviewTable";
import ReviewRatingSummary from "../../components/Reviews/ReviewRatingSummary";
import {
  deleteCustomerReview,
  getAllCustomerReviews,
} from "../../services/ReviewService";
import "../Vendors/style.css";

const ManageCustomerReviews = (props) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState({ page: 1 });
  const [pagination, setPagination] = useState({ last_page: 0 });
  const [ratingFilter, setRatingFilter] = useState("");
  const [summary, setSummary] = useState({
    total: 0,
    average: 0,
    by_rating: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });

  const loadReviews = async (pageNumber, rating = ratingFilter) => {
    setLoading(true);
    try {
      const response = await getAllCustomerReviews(pageNumber, rating);

      if (response.data.success) {
        const payload = response.data.data;
        const paginated = payload.reviews || payload;
        setReviews(paginated.data || []);
        setPagination({ last_page: paginated.last_page || 0 });
        setSummary(
          payload.summary || {
            total: 0,
            average: 0,
            by_rating: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          }
        );
      }
    } catch (error) {
      setReviews([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(props.location?.search || "");
    const pageFromUrl = Number(params.get("page")) || 1;
    const ratingFromUrl = params.get("rating") || "";

    setPage({ page: pageFromUrl });
    setRatingFilter(ratingFromUrl);
  }, [props.location?.search]);

  useEffect(() => {
    loadReviews(page.page, ratingFilter);
  }, [page, ratingFilter]);

  const handleDelete = async (reviewId) => {
    const result = await deleteCustomerReview(reviewId);
    if (result?.success) {
      loadReviews(page.page, ratingFilter);
    }
  };

  const handleRatingChange = (rating) => {
    setRatingFilter(rating);
    setPage({ page: 1 });
    props.history.push(
      rating
        ? `/customer-reviews?page=1&rating=${rating}`
        : "/customer-reviews?page=1"
    );
  };

  const next = () => {
    const currentPage = Number(page.page) + 1;
    setPage({ page: currentPage });
    window.scrollTo(0, 0);
    const ratingQuery = ratingFilter ? `&rating=${ratingFilter}` : "";
    props.history.push(`/customer-reviews?page=${currentPage}${ratingQuery}`);
  };

  const prev = () => {
    let currentPage = Number(page.page) - 1;
    if (currentPage === 0) {
      currentPage = 1;
    }
    setPage({ page: currentPage });
    window.scrollTo(0, 0);
    const ratingQuery = ratingFilter ? `&rating=${ratingFilter}` : "";
    props.history.push(`/customer-reviews?page=${currentPage}${ratingQuery}`);
  };

  const handlePageChange = (pageNumber) => {
    setPage({ page: pageNumber });
    window.scrollTo(0, 0);
    const ratingQuery = ratingFilter ? `&rating=${ratingFilter}` : "";
    props.history.push(`/customer-reviews?page=${pageNumber}${ratingQuery}`);
  };

  const renderPagination = () => {
    const currentPage = Number(page.page);
    const lastPage = Number(pagination.last_page);
    const pagesToShow = 1;
    const paginationItems = [];

    if (currentPage !== 1) {
      paginationItems.push(
        <Button
          key={1}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(1)}
          style={{ marginRight: "10px" }}
        >
          1
        </Button>
      );
    }

    if (currentPage > pagesToShow + 1) {
      paginationItems.push(<span key="ellipsis1">...</span>);
    }

    for (let i = Math.max(2, currentPage - pagesToShow); i < currentPage; i++) {
      paginationItems.push(
        <Button
          key={i}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(i)}
          style={{ margin: "auto 10px" }}
        >
          {i}
        </Button>
      );
    }

    paginationItems.push(
      <Button
        key={currentPage}
        variant="contained"
        className="pageLink"
        disabled
        style={{ marginRight: "10px" }}
      >
        {currentPage}
      </Button>
    );

    for (
      let i = currentPage + 1;
      i <= Math.min(currentPage + pagesToShow, lastPage - 1);
      i++
    ) {
      paginationItems.push(
        <Button
          key={i}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(i)}
          style={{ marginRight: "10px" }}
        >
          {i}
        </Button>
      );
    }

    if (lastPage - currentPage > pagesToShow) {
      paginationItems.push(<span key="ellipsis2">...</span>);
    }

    if (currentPage !== lastPage && lastPage > 0) {
      paginationItems.push(
        <Button
          key={lastPage}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(lastPage)}
          style={{ margin: "auto 10px" }}
        >
          {lastPage}
        </Button>
      );
    }

    return paginationItems;
  };

  if (loading && !reviews.length && !summary.total) {
    return <Loading />;
  }

  return (
    <Wrapper>
      <Typography variant="h5" gutterBottom>
        Customer Reviews
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        All product reviews submitted by customers on the storefront.
      </Typography>

      <ReviewRatingSummary
        summary={summary}
        selectedRating={ratingFilter}
        onRatingChange={handleRatingChange}
      />

      {ratingFilter && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Showing reviews with {ratingFilter} {ratingFilter === "1" ? "star" : "stars"}.
        </Typography>
      )}

      <Paper style={{ marginTop: 16, overflowX: "auto" }}>
        {loading ? (
          <Loading />
        ) : (
          <ReviewTable
            reviews={reviews}
            onDelete={handleDelete}
            showProduct
          />
        )}

        {pagination.last_page >= 2 ? (
          <nav>
            <ul className="paginationMenu">
              <li className="menuItem">
                <Button
                  variant="outlined"
                  disabled={Number(page.page) === 1}
                  className="pageLink"
                  onClick={prev}
                  style={{ marginRight: "10px" }}
                >
                  Previous
                </Button>
              </li>
              {renderPagination()}
              <li className="menuItem">
                <Button
                  variant="outlined"
                  className="pageLink"
                  disabled={Number(page.page) === Number(pagination.last_page)}
                  onClick={next}
                >
                  Next
                </Button>
              </li>
            </ul>
          </nav>
        ) : null}
      </Paper>

      {!loading && reviews.length > 0 && (
        <Typography variant="caption" display="block" style={{ marginTop: 16 }}>
          Tip: open a product from{" "}
          <Link to="/store/our-store">Manage Store</Link> to see reviews for that
          product only.
        </Typography>
      )}
    </Wrapper>
  );
};

export default ManageCustomerReviews;
